/** SDK-independent lifecycle: never queue pre-consent events or replay revoked work. */
export type AnalyticsDriver = {
  setEnabled(enabled: boolean): void;
  event(name: string, parameters: Record<string, string | number | boolean>): void;
  properties(parameters: Record<string, string>): void;
};

export function createConsentAnalyticsTransport(input: {
  allowed(): boolean;
  initialize(): Promise<AnalyticsDriver | null>;
  started(driver: AnalyticsDriver): void;
}) {
  let driver: AnalyticsDriver | null = null;
  let pending: Promise<AnalyticsDriver | null> | null = null;
  let permitted = false;
  let enabled = false;
  let generation = 0;

  function updateEnabled(shouldEnable: boolean) {
    if (!driver || enabled === shouldEnable) return;
    enabled = shouldEnable;
    driver.setEnabled(shouldEnable);
    if (shouldEnable) input.started(driver);
  }

  async function ready(): Promise<AnalyticsDriver | null> {
    if (!input.allowed()) return null;
    if (driver) { updateEnabled(true); return driver; }
    if (!pending) {
      pending = input.initialize().then((loaded) => {
        driver = loaded;
        if (!driver) return null;
        // An opt-out that happens while the SDK loads must take effect before
        // page views, user properties, or the caller's event can be dispatched.
        driver.setEnabled(false);
        enabled = false;
        if (permitted && input.allowed()) updateEnabled(true);
        return driver;
      }).catch(() => null).finally(() => { pending = null; });
    }
    return pending;
  }

  function synchronize(): void {
    const next = input.allowed();
    if (next !== permitted) { permitted = next; generation += 1; }
    if (!next) updateEnabled(false);
    else void ready();
  }

  function event(name: string, parameters: Record<string, string | number | boolean>): void {
    synchronize();
    if (!permitted) return;
    const capturedGeneration = generation;
    void ready().then((loaded) => {
      if (loaded && enabled && capturedGeneration === generation && input.allowed()) loaded.event(name, parameters);
    });
  }

  function properties(parameters: Record<string, string>): void {
    synchronize();
    if (!permitted) return;
    const capturedGeneration = generation;
    void ready().then((loaded) => {
      if (loaded && enabled && capturedGeneration === generation && input.allowed()) loaded.properties(parameters);
    });
  }

  return { synchronize, event, properties };
}
